import { Box, Chip, Stack, Typography, alpha } from "@mui/material";
import { ALL_BUT_ARCHIVED } from "../timeline-filter";
import { Dot, STATUS } from "./timeline-parts";

import type { TimelineFilter, TimelineView } from "../timeline-filter";
import type { FC, ReactNode } from "react";

/** A row of filter chips, with its name before them. */
const FilterRow: FC<{ name: string; children: ReactNode }> = ({ name, children }) => (
  <Stack
    direction="row"
    role="group"
    aria-label={`Filter by ${name.toLowerCase()}`}
    useFlexGap
    sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}
  >
    <Typography
      component="span"
      sx={{
        minWidth: 56,
        mr: 0.5,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        color: "text.secondary",
      }}
    >
      {name}
    </Typography>
    {children}
  </Stack>
);

/** A filter chip with a dot in its colour and its count, pressed when it is on. */
const FilterChip: FC<{
  label: string;
  dot: string;
  count: number;
  pressed: boolean;
  onClick: () => void;
  analyticsId: string;
}> = ({ label, dot, count, pressed, onClick, analyticsId }) => (
  <Chip
    icon={<Dot color={dot} />}
    label={
      <>
        {label}{" "}
        <Box
          component="span"
          sx={{ ml: 0.5, fontSize: 12, fontWeight: 400, color: "text.secondary" }}
        >
          {count}
        </Box>
      </>
    }
    onClick={onClick}
    aria-pressed={pressed}
    variant="outlined"
    sx={{
      height: 32,
      borderRadius: "16px",
      fontSize: 13,
      fontWeight: pressed ? 600 : 500,
      borderColor: pressed ? "primary.main" : "divider",
      bgcolor: (theme) =>
        pressed ? alpha(theme.palette.primary.main, 0.1) : theme.palette.background.paper,
      color: pressed ? "primary.dark" : "text.primary",
      "&.MuiChip-clickable:hover": {
        bgcolor: (theme) =>
          pressed ? alpha(theme.palette.primary.main, 0.14) : theme.palette.action.hover,
      },
      "& .MuiChip-icon": { ml: 1.25, mr: -0.5 },
    }}
    data-analytics-id={analyticsId}
  />
);

/**
 * A chip for each event and each status, with its count, that turns it on and off. "All" turns
 * each one on, or each one off when they are all on.
 */
export const TimelineFilters: FC<{
  view: TimelineView;
  filter: TimelineFilter;
  onChange: (filter: TimelineFilter) => void;
}> = ({ view, filter, onChange }) => (
  <>
    <FilterRow name="Event">
      {view.events.map(({ key, name, color, count, selected }) => (
        <FilterChip
          key={key}
          label={name}
          dot={color ?? "neutral.400"}
          count={count}
          pressed={selected}
          onClick={() =>
            onChange({
              ...filter,
              hiddenEvents:
                key === "all"
                  ? selected
                    ? view.events.map((event) => event.key).filter((each) => each !== "all")
                    : []
                  : selected
                    ? [...filter.hiddenEvents, key]
                    : filter.hiddenEvents.filter((event) => event !== key),
            })
          }
          analyticsId="program-timeline-filter-event"
        />
      ))}
    </FilterRow>
    <FilterRow name="Status">
      {view.statuses.map(({ key, count, selected }) => (
        <FilterChip
          key={key}
          label={key === "all" ? "All statuses" : (STATUS[key]?.label ?? key)}
          dot={STATUS[key]?.dot ?? "neutral.400"}
          count={count}
          pressed={selected}
          onClick={() => {
            // "All statuses" leaves the Archived chip as it is.
            const archived = filter.statuses.filter((status) => status === "archived");
            onChange({
              ...filter,
              statuses:
                key === "all"
                  ? selected
                    ? archived
                    : [...ALL_BUT_ARCHIVED, ...archived]
                  : selected
                    ? filter.statuses.filter((status) => status !== key)
                    : [...filter.statuses, key],
            });
          }}
          analyticsId={`program-timeline-filter-status-${key}`}
        />
      ))}
    </FilterRow>
  </>
);
